import { useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import type React from "react";
import { useCallback, useState } from "react";

import Button from "~/components/buttons/Button";
import CardValueFullWidthWithOptionalBorder from "~/components/Card/components/CardValueFullWidthWithOptionalBorder.tsx";
import type { DeleteEntityTextElements } from "~/components/DeleteEntityView";
import Headline from "~/components/Headline";
import Hint from "~/components/Hint";
import LoadingSpinnerFull from "~/components/LoadingSpinners/LoadingSpinnerFull";
import Modal from "~/components/Modal";
import { removePhaseFromQuota } from "~/modules/client/api/quota/quotaApiDispatchers.ts";
import { CLIENTS_QUOTAS_QUERY_KEY, QUOTA_QUERY_KEY } from "~/modules/client/api/quota/quotaQueries.ts";
import type { ProjectPhaseWithProject } from "~/modules/project/api/projectPhase/projectPhaseTypes.ts";
import { getFullPhaseTitle } from "~/modules/project/utils/projectPhaseUtils.ts";
import { normalizeTKey } from "~/types/typeHelpers.ts";
import { formatCentsToCurrency } from "~/utils/currencyUtils.ts";

type DeleteOrderModalProps = {
	clientId: string;
	isLastPhase: boolean
	isOpen: boolean;
	onCloseClick: () => void;
	projectPhaseData?: ProjectPhaseWithProject
	quotaId: string;

};

const RemovePhaseFromQuotaModal: React.FunctionComponent<DeleteOrderModalProps> = ({
	clientId,
	isLastPhase,
	isOpen,
	onCloseClick,
	projectPhaseData,
	quotaId,
}) => {
	const [busy, setBusy] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState("");

	const queryClient = useQueryClient();

	const handleConfirmationClick = async () => {
		if (!projectPhaseData) return;
		try {
			setBusy(true);
			await removePhaseFromQuota(clientId, quotaId, projectPhaseData.id);
			await queryClient.refetchQueries({ queryKey: QUOTA_QUERY_KEY(quotaId) });
			queryClient.invalidateQueries({ queryKey: CLIENTS_QUOTAS_QUERY_KEY(clientId) });
			queryClient.invalidateQueries();
			handleClose();
		} catch (error) {
			console.log(error);
			setServerErrorMessage("Ein unbekannter Fehler ist aufgetreten.");
		}
		setBusy(false);
	};

	const handleClose = useCallback(() => {
		setBusy(false);
		setServerErrorMessage("");
		onCloseClick();
	}, [onCloseClick]);

	const texts: DeleteEntityTextElements = {
		headerText: "Projektphase entfernen",
		bodyText: "Möchtest Du diese Projektphase wirklich vom Abrechnungskontingent entfernen?",
		confirmationText: "Bist du sicher?",
		deleteButtonText: "Phase entfernen",
	};

	return (
		<Modal open={isOpen}
			   widthClassName="w-[40rem]"
			   setOpen={onCloseClick}>
			<div className="flex flex-col gap-3">
				<Headline type="h4"
						  colorClassName="text-gray-600">{texts.headerText}</Headline>
				{busy && <LoadingSpinnerFull />}
				{!busy && serverErrorMessage && <Hint theme="error">{serverErrorMessage}</Hint>}
				{!busy && !serverErrorMessage && (
					<>
						<div>
							{projectPhaseData &&
								<div className="group mb-4 flex grow items-center justify-between rounded-lg border border-gray-300 px-4">
									<div>
										<Headline type="h5"
												  color="muted">{getFullPhaseTitle(projectPhaseData)}</Headline>
										<div className="text-sm text-gray-500">{projectPhaseData.project.titleWithNumber}</div>
									</div>
									<div className="ml-auto" />
									<div>
										<CardValueFullWidthWithOptionalBorder label="Verfügt"
																			  hasBottomBorder={false}><span className="font-bold text-gray-700">{formatCentsToCurrency(projectPhaseData.budgetCentsTracked)}</span></CardValueFullWidthWithOptionalBorder>
									</div>


								</div>}
							{!isLastPhase && <><p className="mb-2">{texts.bodyText}</p>
								<Hint size="sm">
									Alle bereits getätigten Erfassungen dieser Phase werden dadurch wieder zur Abrechnung freigegeben.
								</Hint>
							</>}
							{isLastPhase &&
								<Hint theme="warning">Einem Abrechnungskontingent muss mindestens eine Phase zugeordnet
									sein. Daher kann diese Phase nicht entfernt werden.</Hint>}
						</div>
						<div className="mt-4">
							<Button theme="danger"
									disabled={isLastPhase}
									onClick={handleConfirmationClick}>
								{texts.confirmationButtonText || "Phase entfernen"}
							</Button>
							<Button theme="none"
									onClick={onCloseClick}>
								{t(normalizeTKey("form:buttonTexts.cancel"))}
							</Button>

						</div>
					</>
				)}
			</div>
		</Modal>
	);
};

export default RemovePhaseFromQuotaModal;
